import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Alert,
  Empty,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import ProductTypeFormModal from "./ProductTypeFormModal";
import { STORES, hashForLabel, labelForHash } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import { DEFAULT_STORE_LABEL, SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = [
  ...STORES.map((s) => ({ value: s.hash, label: `${s.label} — ${s.name}` })),
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const validStoreHashes = STORES.map((s) => s.hash);

const readInitialFilters = (searchParams) => {
  const lsStoreLabel = localStorage.getItem(LS_STORE_KEY);
  const lsStoreHash = hashForLabel(lsStoreLabel);
  const qpStore = searchParams.get("tstore");
  let store_hash;
  if (qpStore === "" || qpStore === null) {
    store_hash = lsStoreHash || hashForLabel(DEFAULT_STORE_LABEL) || "";
  } else {
    store_hash = validStoreHashes.includes(qpStore) ? qpStore : "";
  }

  return {
    store_hash,
    type_name: searchParams.get("tname") || "",
    page: Math.max(parseInt(searchParams.get("tpage"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("tlimit"), 10))
      ? parseInt(searchParams.get("tlimit"), 10)
      : 10,
  };
};

const ProductTypesTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.type_name);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [modalState, setModalState] = useState({ open: false, mode: "create", id: null });

  const abortRef = useRef(null);

  useEffect(() => {
    const label = labelForHash(filters.store_hash);
    if (label) localStorage.setItem(LS_STORE_KEY, label);
  }, [filters.store_hash]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("tstore", filters.store_hash);
    if (filters.type_name) next.set("tname", filters.type_name);
    else next.delete("tname");
    next.set("tpage", String(filters.page));
    next.set("tlimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.type_name === searchInput ? prev : { ...prev, type_name: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchProductTypes(filters, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setRows(items);
      setPagination(pg);
    } catch (err) {
      if (controller.signal.aborted) return;
      const mapped = apiHelpers.mapError(err);
      if (mapped.code === "CANCELLED") return;
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load product types",
        message: mapped.message,
      });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    []
  );

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const resetFilters = () => {
    setSearchInput("");
    setFilters({
      store_hash: hashForLabel(DEFAULT_STORE_LABEL) || "",
      type_name: "",
      page: 1,
      limit: 10,
    });
  };

  const openCreate = () => setModalState({ open: true, mode: "create", id: null });
  const openEdit = useCallback(
    (record) => setModalState({ open: true, mode: "edit", id: record.id }),
    []
  );
  const closeModal = () => setModalState((s) => ({ ...s, open: false }));

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Delete product type?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <span>
            Are you sure you want to permanently delete <b>{record.typeName}</b>? This cannot be undone.
          </span>
        ),
        okText: "Yes",
        cancelText: "No",
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await apiHelpers.deleteProductType(record.id);
            setRows((prev) => prev.filter((r) => String(r.id) !== String(record.id)));
            triggerNotification({
              type: "success",
              title: "Deleted",
              message: `"${record.typeName}" was permanently deleted.`,
            });
            fetchData();
          } catch (err) {
            const mapped = apiHelpers.mapError(err);
            if (mapped.code === "NOT_FOUND") {
              setRows((prev) => prev.filter((r) => String(r.id) !== String(record.id)));
            }
            triggerNotification({
              type: "error",
              title: "Delete failed",
              message: mapped.message,
            });
          }
        },
      });
    },
    [fetchData]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: openEdit,
        onDelete: handleDelete,
        page: filters.page,
        limit: filters.limit,
      }),
    [openEdit, handleDelete, filters.page, filters.limit]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              value={filters.store_hash}
              onChange={(v) => updateFilter({ store_hash: v })}
              options={STORE_OPTIONS}
              style={{ width: 220 }}
              placeholder="Store"
            />
            <Input
              allowClear
              placeholder="Search type name"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 260 }}
              maxLength={255}
            />
            <Select
              value={filters.limit}
              onChange={(v) => updateFilter({ limit: v })}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New Product Type
          </Button>
        </Space>
      </div>

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          rowClassName={(record) =>
            Number(record.isActive) === 0 ? "RowFaded" : ""
          }
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No product types found. Click <b>New Product Type</b> to create one.
                  </span>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.totalPages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 1000, y: 500 }}
        />
      </div>

      <ProductTypeFormModal
        open={modalState.open}
        mode={modalState.mode}
        productTypeId={modalState.id}
        defaultStoreHash={filters.store_hash || hashForLabel(DEFAULT_STORE_LABEL)}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          fetchData();
        }}
      />
    </>
  );
};

export default ProductTypesTable;
